/// <amd-module name="TFS/Admin/Groups/Common/Redux/GroupsViewSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType, ActionWithPayload } from "redux/Utils/ActionHelper";
import { filterXSS } from "xss";
import { addMembersToGroup, checkMembershipExistence } from "TFS/Admin/Common/DataAccessLayers/GraphDAL";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { ISecurityRestClient, SecurityClientName } from "TFS/Admin/Common/RestClient/Security";
import { ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { GroupOperation, IdentityNameSpaceId, IdentityPermissionsWrite, IPermissionPage } from "TFS/Admin/Groups/Common/Constants";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions, GroupsViewActionTypes } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as GroupsActionDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/GroupsPivotPermissionsDataSource";
import { validateMember } from "TFS/Admin/Groups/Common/Redux/DataSource/MemberValidationDataSource";
import * as PermissionsProjectLevelDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/PermissionsProjectLevelDataSource";
import { isCollectionNavigationAllowed } from "TFS/Admin/Groups/Common/Redux/DataSource/ProjectScopedUsersDataSource";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { OrgGroupsBreadcrumbService, OrgGroupsBreadcrumbServiceName } from "TFS/Admin/Groups/Common/Services/OrganizationGroupsBreadcrumbService";
import { getErrorMessage, IErrorAction } from "TFS/Admin/Groups/Common/Util/ErrorMessageHelper";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { AadGraphMember, GraphMember, GraphMembership, GraphSubjectBase, GraphSubjectLookupKey, GraphTraversalDirection } from "VSS/Platform/Contracts/Graph";
import { IVssHistoryService } from "VSS/Platform/History";
import { MessageCardSeverity } from "VSSUI/MessageCard";

export function* groupsViewSaga(): SagaIterator {
    yield effects.takeEvery(GroupsViewActionTypes.InitView, initializeOrganizationGroups);
    yield effects.takeEvery(GroupsViewActionTypes.CheckMembershipExistence, onCheckMembershipExistence);
    yield effects.takeEvery(GroupsViewActionTypes.SaveMembers, onSaveMembers);
    yield effects.takeEvery(GroupsViewActionTypes.LogTelemetry, logTelemetry);

    // TODO - Check if these can be combined as SelectedMemberChanged action as in PermissionsViewSaga
    yield effects.takeEvery(GroupsViewActionTypes.SelectedGroupChanged, selectedGroupChanged);
    yield effects.takeEvery(GroupsViewActionTypes.SelectedUserChanged, selectedUserChanged);
    yield effects.takeEvery(GroupsViewActionTypes.SetPcaGroup, setPcaGroup);
    yield effects.takeEvery(GroupsViewActionTypes.SelectedServicePrincipalChanged, selectedServicePrincipalChanged);
    yield effects.takeEvery(GroupsViewActionTypes.SelectedMemberChanged, selectedMemberChanged);
}

export function* initializeOrganizationGroups(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsView, "Initialize groups view");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const pageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    let projectScopeDescriptor: string | undefined;
    let isUserCollectionNavigationAllowed: boolean = false;

    if (pageData && pageData.project) {
        let descriptorResult;
        try {
            const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
            descriptorResult = (yield effects.call([graphClient, graphClient.getDescriptor], pageData.project.id)) as any;
        } catch (error) {
            descriptorResult = undefined;
        }

        if (!descriptorResult) {
            // project is present but scope descriptor is not present. We cannot use graph APIs, so disable the "Add Group" button
            yield effects.call(telemetryLogger.captureError, pageData.project.id);

            yield effects.put(GroupsPivotActions.isAddGroupDisabled(true));
            yield effects.put(GroupsViewActions.initialLoadSucceeded(isUserCollectionNavigationAllowed, undefined));
            return;
        }

        projectScopeDescriptor = descriptorResult.value;

        // check if the user is project scoped
        isUserCollectionNavigationAllowed = (yield effects.call(isCollectionNavigationAllowed, pageContext)) as any;

        yield effects.put(GroupsViewActions.permissionPage(IPermissionPage.Project));
    }
    yield effects.call(fetchAddGroupPermission, projectScopeDescriptor); // Org add group button scenario
    yield effects.put(GroupsViewActions.initialLoadSucceeded(isUserCollectionNavigationAllowed, projectScopeDescriptor));
}

export function* onCheckMembershipExistence(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.CheckMembershipExistence>): SagaIterator {
    const { member, groupDescriptor } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupMembersPivot, "Check membership");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        if (member.subjectDescriptor) {
            const membershipExistence = (yield effects.call(
                checkMembershipExistence,
                member.subjectDescriptor,
                groupDescriptor,
                telemetryLogger
            )) as any;

            // If the membership already exist, we add the member to the invalid members list
            if (membershipExistence) {
                yield effects.put(GroupsViewActions.addInvalidMember(member));
            } else {
                yield effects.put(GroupsViewActions.addValidMember(member));
            }
        } else {
            // Let user select the subject in this case
            yield effects.put(GroupsViewActions.addValidMember(member));
            yield effects.call(telemetryLogger.captureTelemetry, { originDirectory: member.originDirectory, originId: member.originId });
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* selectedGroupChanged(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SelectedGroupChanged>): SagaIterator {
    if (!action.payload.actionPermissions) {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const descriptor = action.payload.descriptor;

        const permissions = (yield effects.call(GroupsActionDataSource.getGroupPermissions, pageContext, descriptor)) as any;
        yield effects.put(GroupsViewActions.getSelectedGroupPermissionsSucceeded(descriptor, permissions));
    }

    yield effects.call(selectedSubjectChanged, action.payload);
}

export function selectedUserChanged(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SelectedUserChanged>): SagaIterator {
    return innerSelectedMemberChangedInner(action.payload, GroupsViewActions.selectedUserChangedSuccessful(action.payload));
}

export function selectedServicePrincipalChanged(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SelectedServicePrincipalChanged>): SagaIterator {
    return innerSelectedMemberChangedInner(action.payload, GroupsViewActions.selectedServicePrincipalChangedSuccessful(action.payload));
}

export function selectedMemberChanged(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SelectedMemberChanged>): SagaIterator {
    return innerSelectedMemberChangedInner(action.payload, GroupsViewActions.selectedMemberChangedSuccessful(action.payload));
}

function* innerSelectedMemberChangedInner(member: AadGraphMember, effect: ActionWithPayload<GroupsViewActionTypes,AadGraphMember>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    // Check if the service principal is materialized, if not we don't navigate to the permissions page
    const isMaterialized: any = yield effects.call(validateMember, pageContext, {
        descriptor: member.descriptor,
        isMaterializedMember: true
    });

    if (isMaterialized) {
        yield effects.put(effect);
        yield effects.call(selectedSubjectChanged, member);
    }
}

function* fetchAddGroupPermission(projectScopeDescriptor: string): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsView, "Get add group permission");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const securityClient: ISecurityRestClient = (yield effects.call([pageContext, pageContext.getRestClient], SecurityClientName)) as any;

    try {
        if (projectScopeDescriptor) {
            const projectPermissions = (yield effects.call(PermissionsProjectLevelDataSource.getProjectLevelPermissions, pageContext)) as any;

            if (projectPermissions) {
                yield effects.put(GroupsPivotActions.isAddGroupDisabled(!projectPermissions.canEditIdentityInformation));
                yield effects.put(GroupsViewActions.canEditProjectLevelInformation(projectPermissions.canEditProjectLevelInformation));
            } else {
                yield effects.call(telemetryLogger.captureError, "No permission fetched");
            }
        } else {
            const permission = (yield effects.call(
                [securityClient, securityClient.hasPermissions],
                IdentityNameSpaceId,
                IdentityPermissionsWrite,
                "",
                true
            )) as any;
            if (permission && permission.length > 0) {
                yield effects.put(GroupsPivotActions.isAddGroupDisabled(!permission[0]));
            } else {
                yield effects.call(telemetryLogger.captureError, "No permission fetched");
            }
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

function* selectedSubjectChanged(subject: GraphSubjectBase): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsView, "Selected subject changed");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const historyService: IVssHistoryService = (yield effects.call([pageContext, pageContext.getService], "IVssHistoryService")) as any;
    const breadcrumbService: OrgGroupsBreadcrumbService = (yield effects.call(
        [pageContext, pageContext.getService],
        OrgGroupsBreadcrumbServiceName
    )) as any;

    if (subject.descriptor) {
        const url = (yield effects.call([historyService, historyService.generateUrl], { subjectDescriptor: subject.descriptor })) as any;
        yield effects.call([breadcrumbService, breadcrumbService.setSelectedEntityBreadcrumb], subject.displayName, url);
    }
}

export function* onSaveMembers(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SaveMembers>): SagaIterator {
    const { subjectIdentities, selectedGroup, scopeDescriptor } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsView, "Save member");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const resultMembers = (yield effects.call(addMembersToGroup, subjectIdentities, selectedGroup, false, telemetryLogger, scopeDescriptor)) as any;
        let cardSeverity: MessageCardSeverity = MessageCardSeverity.Info;
        let resultMessage = "";

        if (resultMembers) {
            const successCount = resultMembers.success.length;
            if (successCount > 0) {
                yield effects.put(GroupsViewActions.closePCAMessageBar());
                yield effects.put(GroupsPivotActions.updateMemberInfo([selectedGroup], false));
            }
            const failedCount = resultMembers.failed.length;
            if (failedCount > 0) {
                const firstFailed = resultMembers.failed[0].subjectIdentity;
                resultMessage +=
                    failedCount === 1 && !firstFailed.active
                        ? format(Resources.AddMembersToGroupFailedDueToInactiveMember, filterXSS(firstFailed.displayName))
                        : successCount > 0
                            ? format(Resources.AddMembersPartialSuccess, successCount, failedCount)
                            : failedCount > 1
                                ? Resources.AddMembersFailed
                                : getErrorMessage(
                                    GroupsComponent.GroupMembersPivot, // Intentionally GroupMembersPivot here, since we want same errorMessage parsing for Groups' add member scenario
                                    resultMembers.errors.length > 0 ? resultMembers.errors[0] : null,
                                    IErrorAction.Add
                                );
                cardSeverity = MessageCardSeverity.Error;

                // Log all failures for telemetry purposes
                if (resultMembers.errors.length > 0) {
                    const errorString = resultMembers.errors.map((e: any) => `${ParseErrorFromAPI(e)};`).join(" ");
                    yield effects.call(telemetryLogger.captureError, errorString);
                }
            } else {
                resultMessage += format(Resources.AddMembersSuccess, successCount, Resources.Group.toLocaleLowerCase());
            }
        }

        yield effects.put(
            GroupsPivotActions.groupOperationStatus(
                GroupOperation.Add,
                resultMessage,
                cardSeverity === MessageCardSeverity.Info,
                cardSeverity,
                undefined
            )
        );
    } catch (error) {
        yield effects.put(
            GroupsPivotActions.groupOperationStatus(
                GroupOperation.Add,
                getErrorMessage(GroupsComponent.GroupsView, error, IErrorAction.Add),
                true,
                MessageCardSeverity.Error
            )
        );
        yield effects.call(telemetryLogger.captureError, error.message || error);
    } finally {
        yield effects.put(GroupsViewActions.closeAddMemberPanel());
    }
}

export function* setPcaGroup(action: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.SetPcaGroup>): SagaIterator {
    const { pcaGroup, wellKnownGroups } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsView, "getPCAMembershipTraversal");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

        const memberships: GraphMembership[] = (yield effects.call(
            [graphClient, graphClient.listMemberships],
            pcaGroup.descriptor,
            GraphTraversalDirection.Down
        )) as any;

        // const lookupKeys = [{ descriptor: pcaGroup.descriptor }];
        const lookupKeys: GraphSubjectLookupKey[] = memberships.map(membership => ({ descriptor: membership.memberDescriptor }));
        const graphMembers = (yield effects.call([graphClient, graphClient.lookupSubjects], { lookupKeys })) as any;

        let members: GraphMember[] = Object.keys(graphMembers).map(descriptor => graphMembers[descriptor]);

        // Remove well known groups
        members = members.filter(x => wellKnownGroups.findIndex(y => y.descriptor === x.descriptor) < 0);
        const numberOfUsers = members.filter(x => x.subjectKind === "user").length;
        const numberOfGroups = members.length - numberOfUsers;

        // Check if there are one or more non well known group or more than one user
        if (numberOfGroups >= 1 || numberOfUsers > 1) {
            return;
        }

        yield effects.put(GroupsViewActions.setPcaGroupSuccess(pcaGroup));
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* logTelemetry(actionName: ActionsOfType<GroupsViewActions, GroupsViewActionTypes.LogTelemetry>): SagaIterator {
    const { component, action, properties, errorMessage } = actionName.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, component, action);
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        if (properties && Object.keys(properties).length > 0) {
            yield effects.call(telemetryLogger.captureTelemetry, properties);
        }

        if (errorMessage) {
            yield effects.call(telemetryLogger.captureError, errorMessage);
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, errorMessage);
    }
}
